import {
  isStoryblokRichTextPresent,
  storyblokRichTextToPlainText,
} from '@utils/renderStoryblokRichText';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';

import { csrClient } from '../../apollo/api/avantArteClient';
import { AvantArteArtistsSearchGateway } from '../../gateways/AvantArteArtistsSearchGateway';
import { ArtistIdStoryblok } from '../../types/generated-storyblok';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { getArtistTileOverline } from '../../utils/artists';
import { fetchAllProducts } from '../../utils/shopify/fetchAllProducts';
import { fetchStories } from '../../utils/storyblok/stories';
import { ArtistSubscription } from '../Content/ArtistSubscription/ArtistSubscription';
import { ArticleItem, ArticleItemSkeleton } from './ArticleItemSkeleton';

const artistsGateway = new AvantArteArtistsSearchGateway(csrClient);

export const ContentArtist = ({
  blok,
  variant,
}: {
  blok: ArtistIdStoryblok;
  variant: 'card' | 'list';
}) => {
  const { t } = useTranslation('artists');
  const [emitter] = useAnalyticsEmitter();

  const [artist, setArtist] = useState<ArtistPageContent>();
  const [loading, setLoading] = useState(true);
  const [internalId, setInternalId] = useState('');
  const [workCount, setWorkCount] = useState(0);

  const handleClick = useCallback(() => {
    emitter('', 'artist clicked', {
      'artist clicked name': artist?.name,
    });
  }, [artist, emitter]);

  useEffect(() => {
    fetchStories({
      byUuids: 'id' in blok.artist ? blok.artist.id : undefined,
      page: 1,
      perPage: 1,
    }).then(([story]: ArtistPageContent[]) => {
      if (!story) {
        return;
      }
      setArtist(story);
      artistsGateway.getArtistsBySlug([story.slug]).then(([result]) => {
        setInternalId(result.id);
      });
      fetchAllProducts(
        `tag:'${story.content.name}' AND tag:'Listing page'`,
      ).then((works) => {
        setWorkCount(works.length);
        setLoading(false);
      });
    });
  }, [blok.artist]);

  if (loading || !artist) {
    return <ArticleItemSkeleton variant={variant} />;
  }

  return (
    <ArticleItem
      variant={variant}
      image={{
        alt: artist.content.thumbnail.alt,
        filename: artist.content.thumbnail.filename,
        focus: artist.content.thumbnail.focus,
        ratio: '4/3',
      }}
      href={`/${artist.full_slug}`}
      onClick={handleClick}
      title={artist.name}
      description={getArtistTileOverline(
        workCount,
        artist.content.hideCollaborations || null,
        t,
      )}
      text={
        isStoryblokRichTextPresent(artist.content.practiceOverview)
          ? storyblokRichTextToPlainText(artist.content.practiceOverview)
          : undefined
      }
      suffix={
        <ArtistSubscription
          buttonLocation="story"
          internalId={internalId}
          name={artist.name}
          thumbnail={artist.content.thumbnail.filename}
        />
      }
    />
  );
};
